import { useState } from 'react';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import useScrollReveal from '../../hooks/useScrollReveal';
import { getIconFallbackLabel, getUiText } from '../../lib/uiText';
import styles from './SecondaryFeatures.module.css';

const ITEMS_PER_PAGE = 5;

const SecondaryFeatures = ({ data }) => {
  const { locale } = useGlobal();
  const titulo = data?.titulo;
  const features = data?.features || [];
  const [page, setPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState('');
  const [ref, isVisible] = useScrollReveal({ threshold: 0.2 });

  if (!features.length) return null;

  const totalPages = Math.ceil(features.length / ITEMS_PER_PAGE);
  const visibleFeatures = features.slice(
    page * ITEMS_PER_PAGE,
    (page + 1) * ITEMS_PER_PAGE
  );

  const handlePageChange = (newPage, direction) => {
    if (isAnimating) return;
    setSlideDirection(direction);
    setIsAnimating(true);
    
    setTimeout(() => {
      setPage(newPage);
      setSlideDirection(direction === 'left' ? 'enter-right' : 'enter-left');
      
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDirection('');
      }, 300);
    }, 250);
  };

  const handlePrev = () => handlePageChange(Math.max(0, page - 1), 'right');
  const handleNext = () => handlePageChange(Math.min(totalPages - 1, page + 1), 'left');

  const listClassName = [
    styles.secondaryFeatures__list,
    slideDirection === 'left' ? styles['secondaryFeatures__list--exit-left'] : '',
    slideDirection === 'right' ? styles['secondaryFeatures__list--exit-right'] : '',
    slideDirection === 'enter-left' ? styles['secondaryFeatures__list--enter-left'] : '',
    slideDirection === 'enter-right' ? styles['secondaryFeatures__list--enter-right'] : '',
  ].filter(Boolean).join(' ');

  return (
    <section 
      ref={ref}
      className={`${styles.secondaryFeatures} ${isVisible ? styles['secondaryFeatures--visible'] : ''}`}
    >
      <div className={styles.secondaryFeatures__header}>
        <h3 className={styles.secondaryFeatures__title}>{titulo}</h3>
        {totalPages > 1 && (
          <div className={styles.secondaryFeatures__nav}>
            <button
              onClick={handlePrev}
              disabled={page === 0 || isAnimating}
              className={styles.secondaryFeatures__navBtn}
              aria-label={getUiText(locale, 'previous')}
            >
              ‹
            </button>
            <button
              onClick={handleNext}
              disabled={page === totalPages - 1 || isAnimating}
              className={styles.secondaryFeatures__navBtn}
              aria-label={getUiText(locale, 'next')}
            >
              ›
            </button>
          </div>
        )}
      </div>

      <ul className={listClassName}>
        {visibleFeatures.map((feature, index) => {
          const iconMedia = feature?.icon?.url
            ? getStrapiMediaAttributes(feature.icon, {
              altFallback: getIconFallbackLabel(locale, 'feature', feature.label),
              titleFallback: getIconFallbackLabel(locale, 'feature', feature.label),
            })
            : null;

          return (
            <li
              key={feature.id || index}
              className={styles.secondaryFeatures__item}
              style={{ transitionDelay: `${index * 0.05}s` }}
            >
              {iconMedia?.src && (
                <img
                  src={iconMedia.src}
                  alt={iconMedia.alt}
                  title={iconMedia.title}
                  className={styles.secondaryFeatures__icon}
                />
              )}
              <span className={styles.secondaryFeatures__label}>
                {feature.label}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default SecondaryFeatures;
